// hooks/user/useUpdateUserVerification.js
import { useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import useToast from "../useToast";

/**
 * Custom hook for updating user verification status (admin only)
 *
 * Implements admin-only access controls, audit logging, and comprehensive error handling.
 * Requirements: 11.1, 11.2, 11.3, 11.4, 11.5
 *
 * @returns {Object} Mutation object with updateVerification function
 */
export const useUpdateUserVerification = () => {
  const { getAccessTokenSilently } = useAuth0();
  const apiUrl = process.env.REACT_APP_API_URL;
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    async ({ userId, verified, reason }) => {
      const token = await getAccessTokenSilently();

      const response = await axios.put(
        `${apiUrl}/api/admin/users/${userId}/verify`,
        {
          verified,
          reason:
            reason ||
            `Verification ${verified ? "granted" : "revoked"} by admin`,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          timeout: 10000, // 10 second timeout
        }
      );

      return response.data;
    },
    {
      retry: (failureCount, error) => {
        // Don't retry authorization errors or validation errors
        if (
          error.response?.status === 401 ||
          error.response?.status === 403 ||
          error.response?.status === 400
        ) {
          return false;
        }
        return failureCount < 2; // Retry other errors up to 2 times
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
      onSuccess: (data, variables) => {
        const { userId, verified } = variables;
        toast.success(
          `User verification ${verified ? "granted" : "revoked"} successfully!`,
          { duration: 3000 }
        );

        // Invalidate relevant queries to update UI
        queryClient.invalidateQueries(["userProfile", userId]);
        queryClient.invalidateQueries(["userById", userId]);
        queryClient.invalidateQueries("adminUsersList");
        queryClient.invalidateQueries("fetchUser");
        queryClient.invalidateQueries(["user", userId]);

        // Update any cached user data
        queryClient.setQueryData(["userProfile", userId], (oldData) => {
          if (oldData) {
            return { ...oldData, verified };
          }
          return oldData;
        });

        queryClient.setQueryData(["user", userId], (oldData) => {
          if (oldData) {
            return { ...oldData, verified };
          }
          return oldData;
        });
      },
      onError: (error, variables) => {
        let errorMessage =
          "Failed to update verification status. Please try again.";

        if (error.code === "ECONNABORTED") {
          errorMessage =
            "Request timed out. Please check your connection and try again.";
        } else if (error.response) {
          const status = error.response.status;
          const serverMessage = error.response?.data?.message;

          if (status === 401 || status === 403) {
            errorMessage = "You don't have permission to perform this action.";
          } else if (status === 404) {
            errorMessage = "User not found.";
          } else if (status === 400) {
            errorMessage =
              serverMessage ||
              "Invalid request. Please check the data and try again.";
          } else if (status >= 500) {
            errorMessage = "Server error. Please try again in a moment.";
          } else if (serverMessage) {
            errorMessage = serverMessage;
          }
        } else if (error.request) {
          errorMessage =
            "Network error. Please check your connection and try again.";
        }

        toast.error(errorMessage, { duration: 5000 });
      },
    }
  );
};
